import { useIntl, weenatIntl as wintl } from '@weenat/wintl';
import isBoolean from 'lodash-es/isBoolean';
import isDate from 'lodash-es/isDate';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import union from 'lodash-es/union';
import moment from 'moment-timezone';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { useClient } from '../../../ClientProvider';
import { OptionCode, OrgRole } from '../../../enums';
import { useCropTracking } from '../../../hooks/useCropTracking';
import { mergeQueryState } from '../../../mergeState';
import { FARMER_PREFIX, GREEN_BEAN_APRIL_SOWING_DEPT, GREEN_BEAN_MAY_SOWING_DEPT, PLOT_PREFIX, cornTypes, isCorn, isGreenBean, isSeedCorn, isSeedCornCharacteristics, isSorgho, isSweetCorn, isWheat, wbArvalisDepth, wbArvalisHydromorphy, wbArvalisLimestone, wbArvalisSoilTexture, wbArvalisStoniness } from '../../../resources/waterBalanceArvalis.type';
import schemas from '../../../schemas';
import useMutation from '../../../useMutation';
import useQuery from '../../../useQuery';
import navReducer from '../../alerts/navReducer';
import { areDatesChronological } from '../../history/datesUtilities';
import { useReactivableOption, useRemainingToken } from '../../options';
import useOrderedFields from '../../useOrderedFields';
import { WEENAT_ARVALIS_CONSTRUCTOR_ID } from './constants';
import { useArvalisClientIdFromOrg } from './useArvalisClientIdFromOrg';
export { WEENAT_ARVALIS_CONSTRUCTOR_ID };
export { default as useArvalisClientCreation } from './useArvalisClientCreation';
export { useArvalisFarmerCreation } from './useArvalisFarmerCreation';
/**
 * Hook that fetch and return all the different arvalis ids given a weenat plot
 */
export const useArvalisIds = (weenatPlot, enabled = true) => {
    const client = useClient();
    const orgRequest = useQuery(client.orgs.get(weenatPlot.organizationId), {
        enabled: !isNil(weenatPlot.organizationId) && enabled
    });
    const org = !isNil(orgRequest.data) ? orgRequest.data : undefined;
    const clientId = useArvalisClientIdFromOrg(org);
    const cropTypeFromOption = isNil(org) ? undefined : org.optionWBArvalisMonoCropCrop;
    let cropRestrictedList = undefined;
    if (!isNil(cropTypeFromOption)) {
        if (isCorn(cropTypeFromOption)) {
            cropRestrictedList = [...cornTypes];
        }
        else {
            cropRestrictedList = [cropTypeFromOption];
        }
    }
    const currentSuffix = new Date().getFullYear();
    const previousSuffix = currentSuffix === 2023 ? '' : `_${currentSuffix - 1}`;
    return {
        arvalisPlotId: `${PLOT_PREFIX}${weenatPlot.id}_${currentSuffix}`,
        previousArvalisPlotId: `${PLOT_PREFIX}${weenatPlot.id}${previousSuffix}`,
        farmerId: `${FARMER_PREFIX}${org?.id}`,
        clientId,
        orgRequest,
        cropRestrictedList
    };
};
export { useArvalisClientIdFromOrg };
const getBefore = (plot) => `${moment().tz(plot.timezone).add(7, 'day').startOf('day').unix()}`;
export const useStressDaysPerStages = (plot, stages) => {
    const { arvalisPlotId, clientId } = useArvalisIds(plot, true);
    const client = useClient();
    const seasonStressAssessmentRequest = useQuery(client.waterBalanceArvalis.getSeasonStressAssessment({
        clientId: clientId,
        plotId: arvalisPlotId
    }), { enabled: !isNil(clientId) });
    const seasonStressAssessment = seasonStressAssessmentRequest.data;
    const stressDaysPerStages = {
        stressStages: [],
        overallStress: 0
    };
    if (!isNil(seasonStressAssessment)) {
        seasonStressAssessment.forEach((assessment) => {
            if (assessment.startDate.getTime() != stages.at(0)?.date.getTime() &&
                assessment.endDate.getTime() != stages.at(-1)?.date.getTime()) {
                stressDaysPerStages.stressStages.push({
                    droughtCoefficient: assessment.needCoverage,
                    end: { date: assessment.endDate, stage: assessment.endingStage },
                    start: { date: assessment.startDate, stage: assessment.startingStage },
                    stressDayCount: assessment.numberOfStressDays
                });
            }
            else {
                stressDaysPerStages.overallStress = assessment.numberOfStressDays;
            }
        });
    }
    return stressDaysPerStages;
};
export const useWaterBalanceOptionCode = (plot) => {
    let finalCode = OptionCode.waterBalance;
    const activeOptionAndActivableOptions = union(plot.activeOptions, plot.reactivableOptions);
    if (activeOptionAndActivableOptions.includes(OptionCode.waterBalanceRI)) {
        finalCode = OptionCode.waterBalanceRI;
    }
    else if (
    //there is a complexity here because we can go from mon to multi between two year and in this case we will have multi in active option and mono in reactivable option the year of the switch and both of them the next year
    // in tere is a choice to make first it as to be the active one and then the reactivable one if they are both reactivable we pick multi
    //in case of arvalis mono multi inside active or reactivable option
    activeOptionAndActivableOptions.includes(OptionCode.waterBalanceArvalisMono) ||
        activeOptionAndActivableOptions.includes(OptionCode.waterBalanceArvalisMulti)) {
        // first we check if there is an already active mono option
        if (plot.activeOptions.includes(OptionCode.waterBalanceArvalisMono)) {
            finalCode = OptionCode.waterBalanceArvalisMono;
            // then we check if there is an already active multi option or reactivable multi option
        }
        else if (plot.activeOptions.includes(OptionCode.waterBalanceArvalisMulti) ||
            (plot.reactivableOptions?.includes(OptionCode.waterBalanceArvalisMulti) ?? false)) {
            finalCode = OptionCode.waterBalanceArvalisMulti;
            //this case is for reactivable mono option
        }
        else {
            finalCode = OptionCode.waterBalanceArvalisMono;
        }
    }
    return finalCode;
};
export const useWaterBalanceOptionName = (code) => {
    const isArvalis = code === OptionCode.waterBalanceArvalisMono || code === OptionCode.waterBalanceArvalisMulti;
    return wintl.t(isArvalis ? 'models.option.names.OAD-BH-ARVALIS' : `models.option.names.${code}`);
};
export const useWateringFormMutation = ({ isArvalis = false, isEdition = false, plot, onSuccess, reportId, resourceType }) => {
    const client = useClient();
    const { arvalisPlotId, clientId } = useArvalisIds(plot, isArvalis);
    const createMutation = useMutation(client.measurementsReports.create(), { onSuccess });
    const createArvalisRequester = resourceType === 'irrigation'
        ? client.waterBalanceArvalis.addIrrigation
        : client.waterBalanceArvalis.addRainfall;
    const createArvalisMutation = useMutation(createArvalisRequester({
        plotId: arvalisPlotId,
        clientId: clientId
    }), { onSuccess });
    const editMutation = useMutation(client.measurementsReports.update(reportId), {
        onSuccess
    });
    const editArvalisRequester = resourceType === 'irrigation'
        ? client.waterBalanceArvalis.updateIrrigation
        : client.waterBalanceArvalis.updateRainfall;
    const editArvalisMutation = useMutation(editArvalisRequester({
        plotId: arvalisPlotId,
        clientId: clientId
    }), {
        onSuccess
    });
    return useMemo(() => {
        if (isEdition) {
            if (isArvalis) {
                return editArvalisMutation;
            }
            else {
                return editMutation;
            }
        }
        else {
            if (isArvalis) {
                return createArvalisMutation;
            }
            else {
                return createMutation;
            }
        }
    }, [
        isEdition,
        isArvalis,
        editArvalisMutation,
        editMutation,
        createArvalisMutation,
        createMutation
    ]);
};
// used to contain 'HARICOT' & 'EPINARD'
const cropsWithAllStagesEditable = ['PDT', 'SOJA'];
const editableSorghoStages = ['BBCH50'];
const editableWheatStages = ['Z10', 'Z30'];
const editableCornStages = ['BBCH09', 'BBCH63', 'BBCH85'];
const isEditableArvalisStage = ({ code, cropType, org }) => {
    let isEditable = false;
    if (!isNil(cropType)) {
        if (cropsWithAllStagesEditable.includes(cropType)) {
            isEditable = true;
        }
        else if (cropType === 'SORGHO') {
            isEditable = editableSorghoStages.includes(code);
        }
        else if (isWheat(cropType)) {
            isEditable = editableWheatStages.includes(code);
        }
        else if (isCorn(cropType)) {
            isEditable =
                editableCornStages.includes(code) ||
                    (org.myRole === OrgRole.advisor && code === 'BBCH1910' && cropType === 'MAIS_SEMENCE');
        }
    }
    return isEditable;
};
const ARVALIS_CROP_TYPE_NAME_KEY_PATH = 'dss.waterBalance.arvalis_crop_types_names';
/**
 * Get the arvalis crop type name from wintl
 */
export const getArvalisCropTypeName = (cropType) => {
    let trad;
    if (!isNil(cropType)) {
        trad = wintl.t(`${ARVALIS_CROP_TYPE_NAME_KEY_PATH}.${cropType}`);
    }
    return trad;
};
const ARVALIS_STAGE_NAME_KEY_PATH = 'dss.waterBalance.arvalisStages';
/**
 * Get the arvalis stage name from wintl
 */
export const getArvalisStageName = ({ code, cropType }) => {
    let mainKey;
    switch (cropType) {
        case 'MAIS_GRAIN':
        case 'MAIS_FOURRAGE':
            mainKey = 'MAIS_SEMENCE';
            break;
        case 'BD':
            mainKey = 'BT';
            break;
        default:
            mainKey = cropType;
            break;
    }
    let trad;
    if (!isNil(mainKey)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        trad = wintl.t(`${ARVALIS_STAGE_NAME_KEY_PATH}.${mainKey}.${code}`);
    }
    return trad;
};
export function getArvalisStageByDate(arvalisStages, date = moment()) {
    {
        const firstStageAfterTodayIndex = arvalisStages.findIndex((stg) => moment(stg.date).isAfter(date));
        // Next stage should never be 0 but testing ut just in case
        const nextStageIndex = firstStageAfterTodayIndex > -1
            ? firstStageAfterTodayIndex > 0
                ? firstStageAfterTodayIndex
                : 0
            : undefined;
        const currentStage = !isNil(nextStageIndex) && nextStageIndex >= 0
            ? nextStageIndex === 0
                ? undefined
                : arvalisStages[nextStageIndex - 1]
            : arvalisStages[arvalisStages.length - 1];
        return !isNil(currentStage) ? currentStage : undefined;
    }
}
const EMPTY_STAGES = [];
export const useWaterBalanceDetails = ({ plot, isArvalis, isEnabled = true, org }) => {
    const client = useClient();
    const { clientId, orgRequest, arvalisPlotId } = useArvalisIds(plot, isArvalis && isEnabled);
    const arvalisPlotRequest = useQuery(client.waterBalanceArvalis.getPlot({ clientId: clientId, plotId: arvalisPlotId }), { enabled: isArvalis && !isNil(clientId) && isEnabled });
    const fetchedArvalisPlot = arvalisPlotRequest.data;
    const arvalisPlot = useMemo(() => ({
        ...fetchedArvalisPlot,
        clientId: clientId
    }), [clientId, fetchedArvalisPlot]);
    const soilDetailsRequest = useQuery(client.waterBalanceArvalis.getSoil(arvalisPlot.soilCode), {
        enabled: isArvalis && !isNil(arvalisPlot.soilCode) && !isEmpty(arvalisPlot.soilCode) && isEnabled
    });
    const soilDetails = soilDetailsRequest.data;
    const arvalisStagesRequest = useQuery(client.waterBalanceArvalis.getStages({ clientId: clientId, plotId: arvalisPlotId }), { enabled: isArvalis && isEnabled });
    const arvalisStages = arvalisStagesRequest.data || EMPTY_STAGES;
    const finalArvalisStages = useMemo(() => arvalisStages.map((st) => {
        const args = { code: st.code, cropType: arvalisPlot?.cropType };
        return {
            ...st,
            isEditable: isEditableArvalisStage({ ...args, org }),
            name: getArvalisStageName(args)
        };
    }), [arvalisStages, arvalisPlot?.cropType]);
    const { stages, hasNoActiveCycle, lastCrop, waterBalanceConfigRequest, cropLastCycleStagesRequest } = useCropTracking({
        plot,
        isEnabled: !isArvalis && isEnabled
    });
    const waterBalanceConfig = waterBalanceConfigRequest.data;
    const currentStageName = useMemo(() => {
        if (isArvalis) {
            return getArvalisStageByDate(finalArvalisStages)?.name;
        }
        else {
            const firstUndefinedDateIndex = stages.findIndex((x) => isNil(x.date));
            // Next stage should never be 0 but testing ut just in case
            const nextStageIndex = firstUndefinedDateIndex > -1
                ? firstUndefinedDateIndex > 0
                    ? firstUndefinedDateIndex
                    : 0
                : undefined;
            const currentStage = !isNil(nextStageIndex) && nextStageIndex >= 0
                ? nextStageIndex === 0
                    ? stages[0]
                    : stages[nextStageIndex - 1]
                : undefined;
            return !isNil(currentStage) ? currentStage.name : '-';
        }
    }, [stages, isArvalis, finalArvalisStages]);
    const isConfigComplete = useMemo(() => {
        if (!isArvalis) {
            // config is considered complete if the plot has a soil type
            return !isNil(waterBalanceConfig) && !isNil(waterBalanceConfig.soilType);
        }
        else {
            return !isNil(arvalisPlot);
        }
    }, [waterBalanceConfig, arvalisPlot, isArvalis]);
    const { isLoading } = mergeQueryState(arvalisPlotRequest, arvalisStagesRequest, cropLastCycleStagesRequest, waterBalanceConfigRequest, orgRequest, soilDetailsRequest);
    return {
        arvalisPlot,
        arvalisSoilName: !isNil(soilDetails) ? soilDetails.officialName : '-',
        arvalisPlotRequest,
        arvalisStages: finalArvalisStages,
        hasNoActiveCycle,
        isConfigComplete,
        isLoading,
        lastCrop,
        currentStageName,
        waterBalanceConfig
    };
};
export const useWaterBalanceSummary = (plot) => {
    const client = useClient();
    /** Bonduelle agro take precedence */
    const optionCode = useWaterBalanceOptionCode(plot);
    const title = useWaterBalanceOptionName(optionCode);
    const isWaterBalanceRI = optionCode === OptionCode.waterBalanceRI;
    const isArvalis = optionCode === OptionCode.waterBalanceArvalisMono ||
        optionCode === OptionCode.waterBalanceArvalisMulti;
    const { hasNoActiveCycle } = useCropTracking({
        plot,
        before: getBefore(plot),
        isEnabled: !isArvalis
    });
    const { clientId, orgRequest, arvalisPlotId, previousArvalisPlotId } = useArvalisIds(plot, isArvalis);
    const waterBalanceConfigRequest = useQuery(client.waterBalance.getConfig({ plotId: plot.id }), {
        enabled: !isArvalis
    });
    const waterBalanceConfig = waterBalanceConfigRequest.data;
    const arvalisCurrentYearPlotRequest = useQuery(client.waterBalanceArvalis.getPlot({ clientId: clientId, plotId: arvalisPlotId }), {
        enabled: !isNil(clientId) && isArvalis,
        retry: 0
    });
    const arvalisPastYearPlotRequest = useQuery(client.waterBalanceArvalis.getPlot({
        clientId: clientId,
        plotId: previousArvalisPlotId,
        history: true
    }), {
        enabled: !isNil(clientId) && !isNil(arvalisCurrentYearPlotRequest.error) && isArvalis,
        retry: 0
    });
    const haveArvalisPlot = !isNil(arvalisCurrentYearPlotRequest.data);
    const hadArvalisPlotLastYear = !isNil(arvalisPastYearPlotRequest.data);
    const { remainingTokens, optionRequest } = useRemainingToken({
        enabled: isArvalis && !isNil(orgRequest.data?.id),
        optionCode,
        orgId: orgRequest.data?.id
    });
    const arvalisWBResultRequest = useQuery(client.waterBalanceArvalis.getResult({ plotId: arvalisPlotId, clientId: clientId }), {
        enabled: isArvalis && !isNil(clientId) && haveArvalisPlot,
        // we use this call to check if arvalis is configured so no retry on failure
        retry: 0
    });
    const arvalisWBResult = arvalisWBResultRequest.data || undefined;
    let isConfigComplete;
    if (!isArvalis) {
        // config is considered complete if the plot has a soil type
        isConfigComplete = !isNil(waterBalanceConfig) && !isNil(waterBalanceConfig.soilType);
    }
    else {
        isConfigComplete =
            !isNil(arvalisWBResult) &&
                !(arvalisCurrentYearPlotRequest.error?.message === 'Plot code unknown') &&
                arvalisWBResult.status === 'ok';
    }
    const { isLoading } = mergeQueryState(orgRequest, arvalisWBResultRequest, waterBalanceConfigRequest, optionRequest, arvalisCurrentYearPlotRequest, arvalisPastYearPlotRequest);
    const endOfSeasonInMs = arvalisWBResult?.waterBalance?.at(-1)?.date?.getTime() ?? Number.MAX_VALUE;
    const isOver = endOfSeasonInMs < new Date().getTime();
    return {
        isArvalis,
        isConfigComplete,
        isWaterBalanceRI,
        title,
        waterBalanceConfig,
        waterBalanceConfigRequest,
        isLoading,
        hadArvalisPlotLastYear,
        remainingTokens,
        arvalisWaterBalanceStatus: arvalisWBResult?.status,
        arvalisWaterBalanceComputationDate: arvalisWBResult?.computationDate,
        isOver,
        hasNoActiveCycle
    };
};
const defaultToMinInteger = (value) => {
    return isNil(value) ? Number.MIN_SAFE_INTEGER : value;
};
const defineThresholds = ({ timestamp, waterBalanceResults }) => {
    return [
        {
            status: 'comfort',
            value: defaultToMinInteger(waterBalanceResults.HYD_LVL_1[timestamp])
        },
        {
            status: 'vigilance',
            value: defaultToMinInteger(waterBalanceResults.HYD_LVL_2[timestamp])
        },
        {
            status: 'stress',
            value: defaultToMinInteger(waterBalanceResults.DEFI_MAX[timestamp])
        }
    ];
};
export const addToWaterBalanceResult = (entry, acc) => {
    const timestamp = Math.round(entry.date.getTime() / 1000);
    acc.DEFI_MAX[timestamp] = entry.RU * -1;
    acc.HYD[timestamp] = entry.waterDeficit === undefined ? null : entry.waterDeficit * -1;
    acc.HYD_LVL_1[timestamp] = entry.RFU * -1;
    acc.HYD_LVL_2[timestamp] = entry.RFU * -1;
    return acc;
};
export const useWaterBalanceHydricState = ({ plot, isArvalis }) => {
    const client = useClient();
    const { clientId, orgRequest, arvalisPlotId } = useArvalisIds(plot, isArvalis);
    const { waterBalanceResultRequest, hasNoActiveCycle } = useCropTracking({
        plot,
        before: getBefore(plot),
        isEnabled: !isArvalis
    });
    const arvalisWBResultRequest = useQuery(client.waterBalanceArvalis.getResult({ plotId: arvalisPlotId, clientId: clientId }), {
        enabled: isArvalis && !isNil(clientId)
    });
    const arvalisWBResult = arvalisWBResultRequest.data || undefined;
    const daysToTrack = useMemo(() => [
        moment().tz(plot.timezone).startOf('day').unix(),
        moment().tz(plot.timezone).add(3, 'day').startOf('day').unix(),
        moment().tz(plot.timezone).add(6, 'day').startOf('day').unix()
    ], [plot.timezone]);
    const statusPerDay = useMemo(() => {
        const INITIAL_WB_RESULT = {
            DEFI_MAX: {},
            HYD: {},
            HYD_LVL_1: {},
            HYD_LVL_2: {}
        };
        let results = [];
        if (isArvalis) {
            const waterBalanceResults = arvalisWBResult?.waterBalance;
            if (!isNil(waterBalanceResults)) {
                const arvalisResultsAsWBResults = waterBalanceResults?.reduce((acc, entry) => {
                    addToWaterBalanceResult(entry, acc);
                    return acc;
                }, INITIAL_WB_RESULT);
                results = daysToTrack.map((timestamp) => {
                    let status;
                    const thresholds = defineThresholds({
                        timestamp,
                        waterBalanceResults: arvalisResultsAsWBResults
                    });
                    const matchingDataPoint = waterBalanceResults.find((x) => moment(x.date).isSame(moment.unix(timestamp), 'day'));
                    const hydricDeficit = !isNil(matchingDataPoint)
                        ? matchingDataPoint.waterDeficit
                        : undefined;
                    if (!isNil(hydricDeficit)) {
                        status = thresholds.find((level) => hydricDeficit * -1 >= level.value)?.status;
                    }
                    return {
                        status,
                        day: timestamp,
                        value: isNil(hydricDeficit) ? hydricDeficit : hydricDeficit * -1
                    };
                });
            }
        }
        else {
            const waterBalanceResults = waterBalanceResultRequest.data?.data;
            if (!isNil(waterBalanceResults)) {
                results = daysToTrack.map((timestamp) => {
                    let status;
                    const hydricDeficit = waterBalanceResults.HYD[timestamp];
                    const thresholds = defineThresholds({
                        timestamp,
                        waterBalanceResults
                    });
                    if (!isNil(hydricDeficit)) {
                        status = thresholds.find((level) => hydricDeficit >= level.value)?.status;
                    }
                    return { status, day: timestamp, value: hydricDeficit };
                });
            }
        }
        return results;
    }, [arvalisWBResult, waterBalanceResultRequest, daysToTrack, isArvalis]);
    const isConfigComplete = useMemo(() => {
        if (!isArvalis) {
            return !hasNoActiveCycle;
        }
        else {
            return !isNil(arvalisWBResult);
        }
    }, [hasNoActiveCycle, arvalisWBResult, isArvalis]);
    const { isLoading } = mergeQueryState(orgRequest, arvalisWBResultRequest, waterBalanceResultRequest);
    return {
        isConfigComplete,
        statusPerDay,
        isLoading
    };
};
export const useGetArvalisSoilRegion = ({ plotCenter, isEnabled }) => {
    const client = useClient();
    const geoLocRequest = useQuery(client.waterBalanceArvalis.getGeoloc({
        lat: plotCenter?.lat,
        lng: plotCenter?.lng
    }), { enabled: !isNil(plotCenter?.lat) && !isNil(plotCenter?.lng) && isEnabled });
    const geoLoc = geoLocRequest.data;
    return { geoLoc, geoLocRequest };
};
export const useGetArvalisDevice = (plot, orgId) => {
    // this code is used by backend to generate sttion id to use in last resort
    const defaultStationId = plot.id > 6000 ? plot.id : plot.id + 1000000;
    const client = useClient();
    const optionCode = useWaterBalanceOptionCode(plot);
    const plotOptionActivationsRequest = useQuery(client.plotOptionActivations.getMany({
        organizationId: orgId,
        plotIds: [plot.id],
        optionCode: optionCode
    }), { enabled: !isNil(optionCode) });
    const stationId = plotOptionActivationsRequest.data?.results?.[0]?.stationId;
    const device = {
        constructeur: WEENAT_ARVALIS_CONSTRUCTOR_ID,
        code: `${isNil(stationId) ? defaultStationId : stationId}`
    };
    return { device, isLoading: plotOptionActivationsRequest.isLoading };
};
/**
 * Whether or not the given CIPAN is fully filled
 */
const isCipanFilled = (cipan) => {
    return (!isNil(cipan) &&
        !isNil(cipan.startDate) &&
        !isNil(cipan.raisingDate) &&
        !isNil(cipan.destructionDate) &&
        !isNil(cipan.development));
};
export const checkIntermediaryCropFilled = (cipan) => {
    let isFilled = false;
    if (!isNil(cipan) && !isEmpty(cipan)) {
        const { startDate, raisingDate, destructionDate } = cipan;
        isFilled =
            isCipanFilled(cipan) &&
                !isNil(startDate) &&
                !isNil(raisingDate) &&
                !isNil(destructionDate) &&
                areDatesChronological([startDate, raisingDate]) &&
                (moment(destructionDate).isAfter(raisingDate) || moment(raisingDate).isSame(destructionDate));
    }
    return isFilled;
};
const arvalisPlotCreationSteps = [
    'plot_details',
    'agronomic_details_previous_crop',
    'agronomic_details_intermediary_crop',
    'agronomic_details_current_crop',
    'agronomic_details_current_crop_characteristics',
    'irrigation_details',
    'water_balance_data',
    'summary',
    'success'
];
const arvalisPlotCreationStepsWithoutForm = ['success', 'summary'];
const isSubStep = (step) => {
    return step.startsWith('agronomic_details');
};
const STEPS_KEYS_BASE = 'dss.waterBalance.arvalis_plot_creation.steps';
const getStepTitle = (step) => {
    let stepTitle = null;
    if (step !== 'success') {
        if (isSubStep(step)) {
            stepTitle = wintl.t(`${STEPS_KEYS_BASE}.agronomic_details`, { capitalize: true });
        }
        else {
            stepTitle = wintl.t(`${STEPS_KEYS_BASE}.${step}`, { capitalize: true });
        }
    }
    return stepTitle;
};
const getStepSubtitle = (step) => {
    let stepTitle = null;
    if (isSubStep(step) && step !== 'success') {
        stepTitle = wintl.t(`${STEPS_KEYS_BASE}.${step}`, { capitalize: true });
    }
    return stepTitle;
};
export const getDevelopmentDescription = (development) => {
    let description = '';
    switch (development) {
        case 1:
            description = wintl.t(`dss.waterBalance.arvalis_plot_creation.development_descriptions.under_developed`);
            break;
        case 2:
            description = wintl.t(`dss.waterBalance.arvalis_plot_creation.development_descriptions.moderatly_developed`);
            break;
        case 3:
            description = wintl.t(`dss.waterBalance.arvalis_plot_creation.development_descriptions.well_developed`);
            break;
    }
    return description;
};
const HELPERS_MESSAGES_PATH = 'dss.waterBalance.arvalis_plot_creation.steps_helpers';
export const getHelpersPerStep = ({ step, cropType }) => {
    let helper = null;
    if (step !== 'plot_details' &&
        step !== 'summary' &&
        step !== 'water_balance_data' &&
        step !== 'success') {
        if (step === 'agronomic_details_current_crop_characteristics') {
            if (!isNil(cropType) && isSeedCorn(cropType)) {
                helper = wintl.t(`${HELPERS_MESSAGES_PATH}.${step}.seedCorn`);
            }
            else {
                helper = wintl.t(`${HELPERS_MESSAGES_PATH}.${step}.default`);
            }
        }
        else {
            helper = wintl.t(`${HELPERS_MESSAGES_PATH}.${step}`);
        }
    }
    return helper;
};
const initialArvalisPlotCreationSteps = [
    'plot_details',
    'agronomic_details_previous_crop',
    'agronomic_details_current_crop',
    'agronomic_details_current_crop_characteristics',
    'irrigation_details',
    'water_balance_data',
    'summary',
    'success'
];
const initialNavigationState = {
    steps: initialArvalisPlotCreationSteps,
    index: 0,
    history: []
};
export const useArvalisPlotCreation = (weenatPlot) => {
    const [navState, navDispatch] = useReducer(navReducer, initialNavigationState);
    const { clientId, farmerId, arvalisPlotId, orgRequest, cropRestrictedList } = useArvalisIds(weenatPlot);
    const { longitude, latitude } = {
        longitude: !isNil(weenatPlot?.location) ? weenatPlot.location.coordinates[0] : undefined,
        latitude: !isNil(weenatPlot?.location) ? weenatPlot.location.coordinates[1] : undefined
    };
    const { geoLoc, geoLocRequest } = useGetArvalisSoilRegion({
        plotCenter: { lng: longitude, lat: latitude },
        isEnabled: !isNil(longitude) && !isNil(latitude)
    });
    const { region, inseeCode, departmentCode } = {
        region: !isNil(geoLoc) ? geoLoc.region : undefined,
        inseeCode: !isNil(geoLoc) ? geoLoc.inseeCode : undefined,
        departmentCode: !isNil(geoLoc) ? geoLoc.departmentCode : undefined
    };
    const [plotCreationPayload, setPlotCreationPayload] = useState({
        plotName: weenatPlot.name,
        plotId: arvalisPlotId,
        farmerId: farmerId,
        clientId: clientId,
        inseeCode: !isNil(inseeCode) ? inseeCode : undefined,
        longitude,
        latitude,
        useExternalStationForWaterBalance: false
    });
    if (isNil(plotCreationPayload.inseeCode) && !isNil(geoLoc?.inseeCode)) {
        setPlotCreationPayload({ ...plotCreationPayload, inseeCode: geoLoc.inseeCode });
    }
    if (arvalisPlotId !== plotCreationPayload.plotId ||
        farmerId !== plotCreationPayload.farmerId ||
        clientId !== plotCreationPayload.clientId ||
        latitude !== plotCreationPayload.latitude ||
        longitude !== plotCreationPayload.longitude ||
        weenatPlot.name !== plotCreationPayload.plotName) {
        setPlotCreationPayload((currentPayload) => {
            return {
                ...currentPayload,
                plotName: weenatPlot.name,
                plotId: arvalisPlotId,
                farmerId: farmerId,
                clientId: clientId,
                longitude,
                latitude
            };
        });
    }
    const step = useMemo(() => navState.steps[navState.index], [navState.steps, navState.index]);
    const isSuccessStep = step === 'success';
    const isIntermediaryCropFilled = checkIntermediaryCropFilled(plotCreationPayload.cipan);
    const stepTitle = useMemo(() => getStepTitle(step), [step]);
    const subTitle = useMemo(() => getStepSubtitle(step), [step]);
    const helperText = useMemo(() => getHelpersPerStep({ step, cropType: plotCreationPayload.cropType }), [step, plotCreationPayload.cropType]);
    const { isLoading } = mergeQueryState(orgRequest, geoLocRequest);
    return {
        departmentCode,
        goToNextStep: () => navDispatch({
            type: 'goToNextStep'
        }),
        goToPreviousStep: () => navDispatch({
            type: 'goToPreviousStep'
        }),
        helperText,
        isIntermediaryCropFilled,
        isLoading,
        isSuccessStep,
        plotCreationPayload,
        region,
        setPlotCreationPayload,
        step,
        stepIndex: navState.index,
        steps: navState.steps,
        stepTitle,
        subTitle,
        cropRestrictedList
    };
};
/**
 *  Hook that fetches our local Arvalis soils db
 */
export const useGetArvalisSoils = (queryParams) => {
    const client = useClient();
    const { t } = useIntl();
    const soilsRequest = useQuery(client.waterBalanceArvalis.getSoils(queryParams));
    const soils = soilsRequest.data?.results || [];
    const filters = soilsRequest.data?.filters;
    const filtersOptions = useMemo(() => ({
        limestone: wbArvalisLimestone.map((filter) => ({
            value: filter,
            label: filter,
            isDisabled: !isNil(filters) ? !filters.limestone.includes(filter) : false
        })),
        stoniness: wbArvalisStoniness.map((filter) => ({
            value: filter,
            label: filter,
            isDisabled: !isNil(filters) ? !filters.stoniness.includes(filter) : false
        })),
        texture: wbArvalisSoilTexture.map((filter) => ({
            value: filter,
            label: filter,
            isDisabled: !isNil(filters) ? !filters.texture.includes(filter) : false
        })),
        depth: wbArvalisDepth.map((filter) => ({
            value: filter,
            label: filter,
            isDisabled: !isNil(filters) ? !filters.depth.includes(filter) : false
        })),
        hydromorphy: wbArvalisHydromorphy.map((filter) => ({
            value: filter,
            label: filter,
            isDisabled: !isNil(filters) ? !filters.hydromorphy.includes(filter) : false
        }))
    }), [filters]);
    const soilsOptions = useMemo(() => soils.map((arvalisSoil) => ({
        value: arvalisSoil.soilCode,
        label: arvalisSoil.officialName,
        ...arvalisSoil
    })), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(soils)]);
    const filteringProps = useMemo(() => ({
        limestone: {
            name: 'limestone',
            label: t('dss.waterBalance.arvalis_plot_creation.fields.limestone', { capitalize: true }),
            options: filtersOptions.limestone
        },
        stoniness: {
            name: 'stoniness',
            label: t('dss.waterBalance.arvalis_plot_creation.fields.stoniness', { capitalize: true }),
            options: filtersOptions.stoniness
        },
        texture: {
            name: 'texture',
            label: t('dss.waterBalance.arvalis_plot_creation.fields.texture', { capitalize: true }),
            options: filtersOptions.texture
        },
        depth: {
            name: 'depth',
            label: t('dss.waterBalance.arvalis_plot_creation.fields.depth', { capitalize: true }),
            options: filtersOptions.depth
        },
        hydromorphy: {
            name: 'hydromorphy',
            label: t('dss.waterBalance.arvalis_plot_creation.fields.hydromorphy', { capitalize: true }),
            options: filtersOptions.hydromorphy
        }
    }), [
        filtersOptions.depth,
        filtersOptions.hydromorphy,
        filtersOptions.limestone,
        filtersOptions.stoniness,
        filtersOptions.texture,
        t
    ]);
    return {
        soilsRequest,
        soilsOptions,
        filteringProps
    };
};
/**
 * Hook fetching the given crop type varieties
 */
const useGetVarietiesPerCropType = ({ clientId, cropType, departmentCode }) => {
    const client = useClient();
    const varietiesRequest = useQuery(client.waterBalanceArvalis.getVarieties({ clientId, cropType, departmentCode }), 
    /** mais semence and haricot has no variety */
    { enabled: !isGreenBean(cropType) && !isSeedCorn(cropType) });
    const varieties = !isNil(varietiesRequest.data) ? varietiesRequest.data : [];
    return { varietiesRequest, varieties };
};
/**
 * From Arvalis Annex
 */
export const getSowingDateRangePermittedRange = (cropType, departmentCode) => {
    let permittedRange = {};
    const numberDptCode = isNil(departmentCode) || isEmpty(departmentCode)
        ? undefined
        : typeof departmentCode === 'string'
            ? parseInt(departmentCode, 10)
            : departmentCode;
    if (isWheat(cropType)) {
        permittedRange = {
            min: moment().set('date', 10).set('month', 8).subtract(1, 'year').startOf('day').toDate(),
            max: moment().set('month', 2).endOf('month').toDate()
        };
    }
    else if (isCorn(cropType)) {
        permittedRange = {
            min: moment().set('date', 15).set('month', 1).startOf('day').toDate(),
            max: moment().set('month', 6).endOf('month').toDate()
        };
    }
    else if (cropType === 'PDT') {
        permittedRange = {
            min: moment().set('date', 15).set('month', 0).startOf('day').toDate(),
            max: moment().set('date', 15).set('month', 5).endOf('day').toDate()
        };
    }
    else if (cropType === 'SOJA') {
        permittedRange = {
            min: moment().set('month', 2).startOf('month').toDate(),
            max: moment().set('date', 10).set('month', 5).endOf('day').toDate()
        };
    }
    else if (isSorgho(cropType)) {
        permittedRange = {
            min: moment().set('date', 20).set('month', 3).startOf('day').toDate(),
            max: moment().set('date', 10).set('month', 6).endOf('day').toDate()
        };
    }
    else if (isGreenBean(cropType) && !isNil(numberDptCode)) {
        if (GREEN_BEAN_APRIL_SOWING_DEPT.includes(numberDptCode)) {
            permittedRange = {
                min: moment().set('date', 1).set('month', 3).startOf('day').toDate(),
                max: moment().set('month', 6).endOf('month').toDate()
            };
        }
        else if (GREEN_BEAN_MAY_SOWING_DEPT.includes(numberDptCode)) {
            permittedRange = {
                min: moment().set('date', 15).set('month', 4).startOf('day').toDate(),
                max: moment().set('month', 6).endOf('month').toDate()
            };
        }
    }
    return permittedRange;
};
const cropCharacteristicsSchemas = schemas.waterBalance.arvalisPlotCreation;
const sweetCornSchema = cropCharacteristicsSchemas.currentCropDetails.sweetCorn;
const seedCornSchema = cropCharacteristicsSchemas.currentCropDetails.seedCorn;
const sorghoSchema = cropCharacteristicsSchemas.currentCropDetails.sorgho;
const haricotSchema = cropCharacteristicsSchemas.currentCropDetails.haricot;
const defaultCropCharacteristicsSchema = cropCharacteristicsSchemas.currentCropDetails.default;
/**
 * @returns an object containing details for arvalis crop characteristics forms
 */
export const useGetCharacteristicsSchema = ({ cropType, clientId, departmentCode }) => {
    const { varieties, varietiesRequest } = useGetVarietiesPerCropType({
        cropType,
        clientId,
        departmentCode
    });
    const permittedRange = getSowingDateRangePermittedRange(cropType, departmentCode);
    const defaultSowingDate = permittedRange.min;
    if (isSorgho(cropType)) {
        const initialValues = {
            variety: varieties[0]?.code,
            precocity: 'precoce'
        };
        return { schema: sorghoSchema, initialValues, varieties, varietiesRequest };
    }
    else if (isSweetCorn(cropType)) {
        const initialValues = {
            variety: varieties[0]?.code,
            supersweet: false
        };
        return { schema: sweetCornSchema, initialValues, varieties, varietiesRequest };
    }
    else if (isSeedCorn(cropType)) {
        const initialValues = {
            femaleGenitor: varieties[0]?.code,
            maleGenitor: varieties[0]?.code,
            m1SowingDate: defaultSowingDate,
            m2SowingDate: undefined,
            m3SowingDate: undefined,
            gddBtwSowingFlowering: 0,
            numberOfDaysBtwSowingFlowering: 0,
            size: 'petit',
            sowingDevice: ''
        };
        return { schema: seedCornSchema, initialValues, varieties, varietiesRequest };
    }
    else if (isGreenBean(cropType)) {
        return {
            schema: haricotSchema,
            initialValues: { variety: undefined },
            varieties,
            varietiesRequest
        };
    }
    else {
        const initialValues = {
            variety: varieties[0]?.code
        };
        return { schema: defaultCropCharacteristicsSchema, initialValues, varieties, varietiesRequest };
    }
};
// cannot use keyof CropCaracteristics otherwise we have no way to test if it's a CipanFields
const cropCaracteristicsFields = [
    'variety',
    'supersweet',
    'precocity',
    'numberOfDaysBtwSowingFlowering',
    'gddBtwSowingFlowering',
    'size',
    'femaleGenitor',
    'maleGenitor',
    'm1SowingDate',
    'm2SowingDate',
    'm3SowingDate',
    'sowingDevice'
];
// cannot use keyof Cipan otherwise we have no way to test if it's a CipanFields
const cipanFields = ['startDate', 'raisingDate', 'destructionDate', 'development'];
const waterBalanceFields = ['useExternalStationForWaterBalance', 'brand', 'serialNumber'];
const isCipanField = (field) => {
    return cipanFields.includes(field);
};
const isCropCaracteristicsField = (field) => {
    return cropCaracteristicsFields.includes(field);
};
const isWaterBalanceField = (field) => {
    return waterBalanceFields.includes(field);
};
/**
 * @returns an array of ordered fields names for arvalis plot creation forms
 */
const getFieldsPerStep = ({ step, cropType }) => {
    let fields = [];
    switch (step) {
        case 'plot_details':
            fields = ['plotArea', 'soilCode', 'ruMax', 'rfuMax'];
            break;
        case 'agronomic_details_previous_crop':
            fields = ['previousCrop', 'previousWatering'];
            break;
        case 'agronomic_details_current_crop':
            fields = ['cropType', 'sowingDate'];
            break;
        case 'agronomic_details_current_crop_characteristics':
            if (!isNil(cropType)) {
                if (isSorgho(cropType)) {
                    fields = ['variety', 'precocity'];
                }
                else if (isSweetCorn(cropType)) {
                    fields = ['variety', 'supersweet'];
                }
                else if (isSeedCorn(cropType)) {
                    fields = [
                        'numberOfDaysBtwSowingFlowering',
                        'gddBtwSowingFlowering',
                        'size',
                        'femaleGenitor',
                        'maleGenitor',
                        'm1SowingDate',
                        'm2SowingDate',
                        'm3SowingDate',
                        'sowingDevice'
                    ];
                }
                else {
                    fields = ['variety'];
                }
            }
            break;
        case 'agronomic_details_intermediary_crop':
            fields = ['startDate', 'raisingDate', 'destructionDate', 'development'];
            break;
        case 'irrigation_details':
            fields = ['wateringAmount', 'daysBetweenWatering', 'quota'];
            break;
        case 'water_balance_data':
            fields = ['useExternalStationForWaterBalance', 'brand', 'serialNumber'];
            break;
    }
    return fields;
};
const FIELD_LABEL_PATH = 'dss.waterBalance.arvalis_plot_creation.fields';
/**
 * @returns an object of fieldNames mapped with their labels for arvalis plot creation forms
 */
const getFieldsLabelsPerStep = ({ step, cropType }) => {
    let labels = {};
    const varietyLabel = wintl.t('dss.cropTracking.status.precocity_class_label', {
        precocityClassLabel: `(${getArvalisCropTypeName(cropType)})`
    });
    switch (step) {
        case 'plot_details':
            labels = {
                plotArea: wintl.t(`${FIELD_LABEL_PATH}.plotArea`),
                soilCode: wintl.t('soilTextures.soil_texture', { capitalize: true }),
                ruMax: wintl.t(`${FIELD_LABEL_PATH}.ruMax`, { unit: 'mm' }),
                rfuMax: wintl.t(`${FIELD_LABEL_PATH}.rfuMax`, { unit: 'mm' })
            };
            break;
        case 'agronomic_details_previous_crop':
            labels = {
                previousCrop: wintl.t('dss.waterBalance.arvalis_plot_creation.steps.agronomic_details_previous_crop'),
                previousWatering: wintl.t(`${FIELD_LABEL_PATH}.previousWatering`)
            };
            break;
        case 'agronomic_details_current_crop':
            labels = {
                cropType: wintl.t(`${FIELD_LABEL_PATH}.cropType`),
                sowingDate: wintl.t('dss.cropTracking.status.sowing_date')
            };
            break;
        case 'agronomic_details_current_crop_characteristics':
            if (!isNil(cropType)) {
                if (isSorgho(cropType)) {
                    labels = {
                        variety: varietyLabel,
                        precocity: wintl.t(`${FIELD_LABEL_PATH}.precocity`)
                    };
                }
                else if (isSweetCorn(cropType)) {
                    labels = {
                        variety: varietyLabel,
                        supersweet: wintl.t(`${FIELD_LABEL_PATH}.supersweet`)
                    };
                }
                else if (isSeedCorn(cropType)) {
                    labels = {
                        numberOfDaysBtwSowingFlowering: wintl.t(`${FIELD_LABEL_PATH}.numberOfDaysBtwSowingFlowering`),
                        gddBtwSowingFlowering: wintl.t(`${FIELD_LABEL_PATH}.gddBtwSowingFlowering`),
                        size: wintl.t(`${FIELD_LABEL_PATH}.size`),
                        femaleGenitor: wintl.t(`${FIELD_LABEL_PATH}.femaleGenitor`),
                        maleGenitor: wintl.t(`${FIELD_LABEL_PATH}.maleGenitor`),
                        m1SowingDate: wintl.t(`${FIELD_LABEL_PATH}.m1SowingDate`),
                        m2SowingDate: wintl.t(`${FIELD_LABEL_PATH}.m2SowingDate`),
                        m3SowingDate: wintl.t(`${FIELD_LABEL_PATH}.m3SowingDate`),
                        sowingDevice: wintl.t(`${FIELD_LABEL_PATH}.sowingDevice`)
                    };
                }
                else {
                    labels = {
                        variety: varietyLabel
                    };
                }
            }
            break;
        case 'agronomic_details_intermediary_crop':
            labels = {
                startDate: wintl.t(`${FIELD_LABEL_PATH}.startDate`),
                raisingDate: wintl.t(`${FIELD_LABEL_PATH}.raisingDate`),
                destructionDate: wintl.t(`${FIELD_LABEL_PATH}.destructionDate`),
                development: wintl.t(`${FIELD_LABEL_PATH}.development`)
            };
            break;
        case 'water_balance_data':
            labels = {
                useExternalStationForWaterBalance: wintl.t(`${FIELD_LABEL_PATH}.useExternalStationForWaterBalance`),
                brand: wintl.t(`${FIELD_LABEL_PATH}.brand`),
                serialNumber: wintl.t(`${FIELD_LABEL_PATH}.stationId`)
            };
            break;
        case 'irrigation_details':
            labels = {
                wateringAmount: wintl.t(`${FIELD_LABEL_PATH}.wateringAmount`, { unit: 'mm' }),
                daysBetweenWatering: wintl.t(`${FIELD_LABEL_PATH}.daysBetweenWatering`),
                quota: wintl.t(`${FIELD_LABEL_PATH}.quota`)
            };
            break;
    }
    return labels;
};
export const useArvalisPlotCreationFields = ({ step, cropType, formOrSubmit }) => {
    const labels = getFieldsLabelsPerStep({ step, cropType });
    const fields = getFieldsPerStep({ step, cropType });
    const orderedFields = useOrderedFields(fields, formOrSubmit);
    return { orderedFields, labels };
};
/**
 * @returns the formatted value of the given field from the arvalis plot payload
 */
const getPayloadValuesPerField = ({ field, payload, soilName, constructors }) => {
    let rawValue;
    if (isCipanField(field)) {
        rawValue = payload.cipan?.[field];
    }
    else if (isCropCaracteristicsField(field)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        rawValue = payload.cropCharacteristics[field];
    }
    else if (field === 'soilCode') {
        rawValue = soilName;
    }
    else if (isWaterBalanceField(field)) {
        if (field === 'useExternalStationForWaterBalance') {
            rawValue = payload.useExternalStationForWaterBalance
                ? wintl.t('actions.answer_yes')
                : wintl.t('actions.answer_no');
        }
        if (field === 'brand') {
            rawValue = constructors.find((constructor) => {
                return constructor.value === payload.device?.constructeur;
            })?.label;
        }
        if (field === 'serialNumber') {
            rawValue = payload.device?.code;
        }
    }
    else {
        rawValue = payload[field];
    }
    let value;
    if (isDate(rawValue)) {
        value = moment(rawValue).format('DD/MM/YY');
    }
    else if (isBoolean(rawValue)) {
        value = wintl.t(rawValue === true ? 'actions.answer_yes' : 'actions.answer_no');
    }
    else if (isNil(rawValue)) {
        value = '-';
    }
    else {
        value = rawValue;
    }
    return value;
};
const summarizedSections = [
    {
        parentStep: 'plot_details',
        subSteps: ['plot_details']
    },
    {
        parentStep: 'agronomic_details_current_crop',
        subSteps: [
            'agronomic_details_previous_crop',
            'agronomic_details_intermediary_crop',
            'agronomic_details_current_crop',
            'agronomic_details_current_crop_characteristics'
        ]
    },
    {
        parentStep: 'irrigation_details',
        subSteps: ['irrigation_details']
    },
    {
        parentStep: 'water_balance_data',
        subSteps: ['water_balance_data']
    }
];
export const useArvalisPlotCreationSummary = ({ payload, onEditAWC, departmentCode, onPlotCreationError, onPlotCreationSuccess, plot, orgId }) => {
    const { t } = useIntl();
    const client = useClient();
    const { device, isLoading: isLoadingGetArvalisDevice } = useGetArvalisDevice(plot, orgId);
    const { cropType, sowingDate, soilCode, clientId, plotId } = payload;
    const soilCharacteristicsRequest = useQuery(client.waterBalanceArvalis.getCaracteristicsSoil({
        clientId: clientId,
        cropType: cropType,
        deptNumber: parseInt(departmentCode),
        soilCode: soilCode,
        sowingDate: sowingDate
    }), {
        enabled: !isNil(cropType) &&
            !isNil(clientId) &&
            !isNil(soilCode) &&
            !isNil(sowingDate) &&
            !isNil(departmentCode)
    });
    const soilCharacteristics = soilCharacteristicsRequest.data;
    const soilDetailsRequest = useQuery(client.waterBalanceArvalis.getSoil(soilCode), {
        enabled: !isNil(soilCode)
    });
    const soilDetails = soilDetailsRequest.data;
    const initialValues = !isNil(soilCharacteristics)
        ? {
            ruMax: !isNil(payload.ruMax) ? payload.ruMax : soilCharacteristics.RU,
            rfuMax: !isNil(payload.rfuMax) ? payload.rfuMax : soilCharacteristics.RFU,
            soilCode
        }
        : { ruMax: payload.ruMax, rfuMax: payload.rfuMax, soilCode };
    const constructorsQuery = useQuery(client.waterBalanceArvalis.getConstructors({}));
    const constructors = constructorsQuery.data ?? [];
    const sections = summarizedSections.map((section) => {
        const { subSteps, parentStep } = section;
        const data = subSteps.map((step) => {
            if (isNil(payload.cipan) && step === 'agronomic_details_intermediary_crop') {
                return null;
            }
            else {
                const subtitle = getStepSubtitle(step);
                const buttonProps = step === 'plot_details'
                    ? {
                        label: t('dss.waterBalance.arvalis_plot_creation.edit_available_water_capacities'),
                        onPress: () => onEditAWC()
                    }
                    : undefined;
                const labels = getFieldsLabelsPerStep({ step, cropType });
                const fields = getFieldsPerStep({ step, cropType });
                const fieldsFilteredWithUseExternalStationForWaterBalance = fields.filter((e) => {
                    if (!payload.useExternalStationForWaterBalance) {
                        return e !== 'serialNumber' && e !== 'brand';
                    }
                    return e;
                });
                const items = fieldsFilteredWithUseExternalStationForWaterBalance.map((fld) => {
                    const label = labels[fld];
                    const value = getPayloadValuesPerField({
                        field: fld,
                        payload: {
                            ...payload,
                            ...initialValues
                        },
                        soilName: !isNil(soilDetails) ? soilDetails.officialName : '',
                        constructors
                    });
                    return { label, value };
                });
                return { subtitle, items, buttonProps };
            }
        });
        return { title: getStepTitle(parentStep), data };
    });
    const [createArvalisPlot, createArvalisPlotRequest] = useMutation(client.waterBalanceArvalis.addPlot({
        clientId: clientId,
        plotId: plotId
    }), { onError: (e) => onPlotCreationError(e.message), onSuccess: onPlotCreationSuccess });
    const { isLoading } = mergeQueryState(soilCharacteristicsRequest, soilDetailsRequest);
    return {
        createArvalisPlot,
        createArvalisPlotRequest,
        soilCharacteristics,
        isLoading: isLoading || isLoadingGetArvalisDevice,
        sections,
        initialValues,
        device
    };
};
const FORM_UNUSED_KEYS = [
    'clientId',
    'plotId',
    'farmerId',
    'inseeCode',
    'latitude',
    'longitude',
    'plotName',
    'device',
    'wateringDevice',
    'zipCode'
];
export function useArvalisFarmer({ plot, isEnabled }) {
    const client = useClient();
    const { clientId, farmerId } = useArvalisIds(plot);
    const request = useQuery(client.waterBalanceArvalis.getFarmer({
        clientId: clientId,
        farmerId: farmerId
    }), { enabled: !isNil(plot.id) && !isNil(clientId) && !isNil(farmerId) && isEnabled });
    const farmer = request.data;
    return { request, farmer };
}
export const useArvalisWaterBalanceSettings = ({ plot, isEnabled = false, onError, onSuccess, isSeasonResetting = false }) => {
    const client = useClient();
    const { clientId, arvalisPlotId, cropRestrictedList, previousArvalisPlotId } = useArvalisIds(plot);
    const option = useWaterBalanceOptionCode(plot);
    const { request: arvalisFarmerRequest, farmer } = useArvalisFarmer({ plot, isEnabled });
    const arvalisPlotRequest = useQuery(client.waterBalanceArvalis.getPlot({
        clientId: clientId,
        plotId: isSeasonResetting ? previousArvalisPlotId : arvalisPlotId,
        history: isSeasonResetting ? true : undefined
    }), { enabled: !isNil(plot.id) && !isNil(clientId) && isEnabled });
    const [updateArvalisPlot, updateArvalisPlotRequest] = useMutation(client.waterBalanceArvalis.updatePlot({ clientId: clientId, plotId: arvalisPlotId }), { onError: (e) => onError(e.message), onSuccess });
    // consume plot activation
    const { consumeOption } = useReactivableOption({ enabled: true, optionCode: option, plot });
    const [addArvalisPlot, addArvalisPlotRequest] = useMutation(client.waterBalanceArvalis.addPlot({ clientId: clientId, plotId: arvalisPlotId }), {
        onError: (e) => onError(e.message),
        onSuccess: () => {
            // when we get succes from arvalis we decrement option plot usage token
            consumeOption();
            onSuccess();
        }
    });
    const { longitude, latitude } = {
        longitude: !isNil(plot?.location) ? plot.location.coordinates[0] : undefined,
        latitude: !isNil(plot?.location) ? plot.location.coordinates[1] : undefined
    };
    const { geoLoc, geoLocRequest } = useGetArvalisSoilRegion({
        plotCenter: { lng: longitude, lat: latitude },
        isEnabled: !isNil(longitude) && !isNil(latitude) && isEnabled
    });
    const { region: plotRegion, departmentCode } = useMemo(() => ({
        region: geoLoc?.region,
        departmentCode: geoLoc?.departmentCode
    }), [geoLoc]);
    const arvalisPlot = arvalisPlotRequest.data;
    const flatArvalisPlot = useMemo(() => {
        let result = {};
        if (!isNil(arvalisPlot)) {
            const flatPlot = {
                ...arvalisPlot
            };
            FORM_UNUSED_KEYS.forEach((k) => {
                delete flatPlot[k];
            });
            const { cropCharacteristics, cipan, station, ...rest } = flatPlot;
            // removing the unused value between gddBtwSowingFlowering & numberOfDaysBtwSowingFlowering
            if (!isNil(cropCharacteristics) && isSeedCornCharacteristics(cropCharacteristics)) {
                if (!isNil(cropCharacteristics.gddBtwSowingFlowering)) {
                    delete cropCharacteristics['numberOfDaysBtwSowingFlowering'];
                }
                else if (!isNil(cropCharacteristics.numberOfDaysBtwSowingFlowering)) {
                    delete cropCharacteristics['gddBtwSowingFlowering'];
                }
            }
            if (isSeasonResetting) {
                rest.sowingDate = undefined;
            }
            const isWeenatStation = station?.constructeur === WEENAT_ARVALIS_CONSTRUCTOR_ID;
            const externalStation = {
                useExternalStationForWaterBalance: !isWeenatStation,
                serialNumber: !isWeenatStation ? station?.code : undefined,
                brand: !isWeenatStation ? station?.constructeur : undefined
            };
            result = {
                ...cropCharacteristics,
                ...cipan,
                ...externalStation,
                ...rest
            };
        }
        return result;
    }, [arvalisPlot, isSeasonResetting]);
    const onSubmit = useCallback((values) => {
        if (!isNil(arvalisPlot)) {
            const { 
            // Characteristics
            variety, supersweet, numberOfDaysBtwSowingFlowering, gddBtwSowingFlowering, size, femaleGenitor, maleGenitor, m1SowingDate, m2SowingDate, m3SowingDate, sowingDevice, precocity, 
            // Cipan
            startDate, raisingDate, destructionDate, development, cropType, 
            // other
            ...payloadRest } = values;
            const cipanFromFlatPlot = { startDate, raisingDate, destructionDate, development };
            const cropCharacteristicsFromFlatPlot = !isNil(cropType)
                ? isSweetCorn(cropType)
                    ? { variety, supersweet }
                    : isSeedCorn(cropType)
                        ? {
                            variety,
                            // dirty handle of form that default to '' instead of undefined
                            numberOfDaysBtwSowingFlowering: numberOfDaysBtwSowingFlowering === ''
                                ? undefined
                                : numberOfDaysBtwSowingFlowering,
                            gddBtwSowingFlowering: gddBtwSowingFlowering === ''
                                ? undefined
                                : gddBtwSowingFlowering,
                            size,
                            femaleGenitor,
                            maleGenitor,
                            m1SowingDate,
                            m2SowingDate,
                            m3SowingDate,
                            sowingDevice
                        }
                        : isSorgho(cropType)
                            ? {
                                variety,
                                precocity
                            }
                            : { variety }
                : undefined;
            const payload = {
                cipan: isCipanFilled(cipanFromFlatPlot) ? cipanFromFlatPlot : undefined,
                cropCharacteristics: cropCharacteristicsFromFlatPlot,
                cropType,
                ...payloadRest
            };
            FORM_UNUSED_KEYS.forEach((k) => {
                if (k === 'device' && !isNil(payload[k])) {
                    return;
                }
                else if (k === 'clientId') {
                    payload[k] = clientId;
                }
                else {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    payload[k] = arvalisPlot[k];
                }
            });
            if (!isNil(clientId)) {
                if (!isSeasonResetting) {
                    updateArvalisPlot({ ...payload, plotName: plot.name });
                }
                else {
                    // we query based on previous year but add on current year so we overidding plotId
                    addArvalisPlot({
                        ...payload,
                        plotId: arvalisPlotId,
                        plotName: plot.name
                    });
                }
            }
        }
    }, [
        addArvalisPlot,
        arvalisPlot,
        arvalisPlotId,
        clientId,
        isSeasonResetting,
        updateArvalisPlot,
        plot.name
    ]);
    const { isLoading } = mergeQueryState(arvalisPlotRequest, geoLocRequest, arvalisFarmerRequest);
    return {
        arvalisClientId: clientId,
        arvalisPlot,
        farmer,
        flatArvalisPlot,
        isLoading,
        onSubmit,
        plotRegion,
        departmentCode,
        request: isSeasonResetting ? addArvalisPlotRequest : updateArvalisPlotRequest,
        cropRestrictedList
    };
};
export const useArvalisPreviousCrops = (clientId) => {
    const client = useClient();
    const previousCropRequest = useQuery(client.waterBalanceArvalis.getPreviousCropOptions({ clientId: clientId }), { enabled: !isNil(clientId) });
    const previousCrops = !isNil(previousCropRequest.data)
        ? previousCropRequest.data
        : { irrigated: [], notIrrigated: [] };
    return { previousCrops, previousCropRequest };
};
export const isOutsideRURange = ({ inputRU, proposedRU }) => {
    return inputRU < proposedRU - 20 || inputRU > proposedRU + 20;
};
export const keysNotKeepFromYearToYear = ['m1SowingDate', 'm2SowingDate', 'm3SowingDate'];
export const waterBalanceEventIdPrefixes = {
    realRainfalls: 'real-rainfalls-',
    rainfallsReport: 'rainfalls-report-',
    irrigationReport: 'irrigation-report-'
};
export function fromIrrigationReportsToCalendarEvents({ reports, timezone, color }) {
    return reports.map((irrigationReport) => {
        const eventDate = moment.unix(parseInt(irrigationReport.timestamp, 10)).tz(timezone).toDate();
        return {
            id: `${waterBalanceEventIdPrefixes.irrigationReport}${irrigationReport.id.toString()}`,
            originalId: irrigationReport.id,
            // TODO: handle preferred units
            name: `${irrigationReport.value} mm`,
            start: eventDate,
            end: eventDate,
            color: color
        };
    });
}
export function fromRainfallsReportsToCalendarEvents({ reports, timezone, color }) {
    return reports.reduce((acc, rainfallsReport) => {
        if (!isNil(rainfallsReport.value)) {
            const eventDate = moment.unix(rainfallsReport.timestamp).tz(timezone).toDate();
            acc.push({
                id: `${rainfallsReport.isOverride
                    ? waterBalanceEventIdPrefixes.rainfallsReport
                    : waterBalanceEventIdPrefixes.realRainfalls}${rainfallsReport.timestamp.toString()}`,
                originalId: rainfallsReport.reportId,
                // TODO: handle preferred units
                name: `${rainfallsReport.value} mm ${rainfallsReport.isOverride ? '*' : ''}`.trim(),
                start: eventDate,
                end: eventDate,
                color: color
            });
        }
        return acc;
    }, []);
}
export const getEventReportType = (eventId) => {
    if (eventId.includes(waterBalanceEventIdPrefixes.realRainfalls)) {
        return 'realRainfalls';
    }
    else if (eventId.includes(waterBalanceEventIdPrefixes.rainfallsReport)) {
        return 'rainfallsReport';
    }
    else if (eventId.includes(waterBalanceEventIdPrefixes.irrigationReport)) {
        return 'irrigationReport';
    }
    else {
        throw new Error('Could not extract event report type');
    }
};
